/**
 * Step - Warranty Selection
 *
 * Standalone step for selecting a warranty template and option.
 * Reuses the WarrantySelector from shared.tsx.
 */

import { useEffect, useMemo } from 'react'
import { Filter, ShieldCheck } from 'lucide-react'
import type { WizardState } from '@/admin/pages/registrations/new-registration-page'
import type {
  WarrantyFilterSummary,
  WarrantyTemplateSummary,
} from '@/admin/types/products'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useWarrantyAvailability } from '@/admin/hooks/use-warranty-availability'
import {
  availabilityKey,
  type AvailabilityResult,
} from '@/admin/hooks/use-availability-rules'
import { RegistrationPanel, RegistrationStepActions, RegistrationStepGrid } from './layout'
import {
  WarrantySelector,
  formatWarrantyTypeLabel,
  getDefaultWarrantyOptionUuid,
  getOptionAvailability,
  hasSelectableWarrantyOption,
  isWarrantySelectionResolved,
} from './shared'
import {
  useRegisterValidator,
  useWizardValidation,
} from './wizard-validation'

interface StepProps {
  state: WizardState
  update: (payload: Partial<WizardState>) => void
  next: () => void
  back?: () => void
  continueDisabled?: boolean
  continueHint?: string | null
  warrantyTemplates: WarrantyTemplateSummary[]
  /**
   * Server-supplied diagnostic from `/registrations/metadata/form-fields/`.
   * Drives the empty-state hint when no warranties are available - lists
   * the filter rules that narrowed the set, the host-attribute inputs
   * that drove strict-mode resolution, and the count of templates the
   * resolver started from. Optional so consumer/legacy callers without
   * the field still type-check.
   */
  filterSummary?: WarrantyFilterSummary | null
  /**
   * Registration-field code_names captured on this (warranty) step or
   * later. They are excluded from the per-option availability evaluation
   * context: no availability rule reads them, and they're collected
   * at/after warranty selection so they can't gate which warranties are
   * available. Without this, every keystroke in a warranty-step field
   * (e.g. Solar PROtect inputs) changes the evaluation request body,
   * re-runs the rule check, and flickers the warranty list.
   */
  excludedAvailabilityFieldCodes?: Set<string>
  /**
   * Extra panel(s) to render alongside this step's own panel in the
   * shared `RegistrationStepGrid`.
   */
  extraPanels?: React.ReactNode
}

export function StepWarranty({
  state,
  update,
  next,
  back,
  // The legacy `continueDisabled` prop is intentionally not
  // destructured. Continue is always enabled; warranty selection is
  // gated via the wizard validation context.
  continueHint,
  warrantyTemplates,
  excludedAvailabilityFieldCodes,
  filterSummary,
  extraPanels,
}: StepProps) {
  const { can } = usePermissions()
  const canRegisterExtended = can('warranties.register_extended')

  const permittedTemplates = useMemo(() => {
    if (canRegisterExtended) return warrantyTemplates
    return warrantyTemplates.filter(
      (wt) => wt.warranty_type !== 'EXTENDED_WARRANTY',
    )
  }, [warrantyTemplates, canRegisterExtended])

  // Registration-field values that may participate in availability rules:
  // everything except fields captured on the warranty step or later, which
  // are pure post-selection data capture (e.g. Solar PROtect inputs).
  // Filtering them here keeps the availability-evaluation request body
  // stable while the user fills those fields, so the warranty list doesn't
  // re-evaluate and flicker on every keystroke.
  const availabilityRegistrationFieldValues = useMemo(() => {
    if (!excludedAvailabilityFieldCodes || excludedAvailabilityFieldCodes.size === 0) {
      return state.registrationFieldValues
    }
    const filtered: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(state.registrationFieldValues || {})) {
      if (!excludedAvailabilityFieldCodes.has(k)) filtered[k] = v
    }
    return filtered
  }, [state.registrationFieldValues, excludedAvailabilityFieldCodes])

  // Pass every collected value through verbatim - rules reference fields
  // by code_name, so we don't single out any tenant-specific attribute.
  const fields = useMemo(
    () => ({
      consumer_email: state.consumerEmail,
      consumer_name: [state.consumerFirstName, state.consumerLastName]
        .filter(Boolean)
        .join(' '),
      consumer_phone: state.consumerPhone,
      ...state.dynamicAttributes,
      ...state.earlyAttributes,
      ...availabilityRegistrationFieldValues,
    }),
    [
      state.consumerEmail,
      state.consumerFirstName,
      state.consumerLastName,
      state.consumerPhone,
      state.dynamicAttributes,
      state.earlyAttributes,
      availabilityRegistrationFieldValues,
    ],
  )

  const entities = useMemo(
    () => ({
      host: {
        ...state.hostAttributeValues,
        ...availabilityRegistrationFieldValues,
      },
    }),
    [state.hostAttributeValues, availabilityRegistrationFieldValues],
  )

  const availability = useWarrantyAvailability({
    templates: permittedTemplates,
    productUuid: state.selectedProduct?.uuid ?? null,
    productTemplateUuid: state.selectedProduct?.product_template?.uuid ?? null,
    fields,
    entities,
  })

  // Hide warranty classes whose options are all blocked by availability
  // rules - selecting them can never produce a complete (template +
  // option) selection, which would leave the user stuck with Continue
  // disabled and no actionable feedback.
  const visibleTemplates = useMemo(
    () =>
      permittedTemplates.filter((wt) => hasSelectableWarrantyOption(wt, availability)),
    [permittedTemplates, availability],
  )

  // If the currently-selected option becomes unavailable due to a state
  // change, clear it so the user is forced to pick a valid one.
  useEffect(() => {
    if (!state.warrantyTemplateUuid || !state.warrantyTemplateOptionUuid) return
    const decision = availability.byOption.get(
      availabilityKey(state.warrantyTemplateUuid, state.warrantyTemplateOptionUuid),
    )
    if (decision && !decision.available) {
      update({ warrantyTemplateOptionUuid: null })
    }
  }, [
    availability.byOption,
    state.warrantyTemplateUuid,
    state.warrantyTemplateOptionUuid,
    update,
  ])

  // If the currently-selected warranty class is no longer visible (its
  // options were filtered out by availability), drop the selection so
  // the UI stays consistent.
  useEffect(() => {
    if (!state.warrantyTemplateUuid) return
    const stillVisible = visibleTemplates.some((t) => t.uuid === state.warrantyTemplateUuid)
    if (!stillVisible) {
      update({ warrantyTemplateUuid: null, warrantyTemplateOptionUuid: null })
    }
  }, [visibleTemplates, state.warrantyTemplateUuid, update])

  // When only one template survives RBAC + availability narrowing, the
  // single-class summary card in WarrantySelector is non-interactive -
  // the user has nothing to click. Auto-resolve both class and option
  // here (atomically, in one update) so ``isWarrantySelectionResolved``
  // can flip true and Continue actually advances. Mirrors the page-
  // level auto-select, but keyed off the *step's* visible list so it
  // survives any narrowing the page-level effect couldn't see (e.g.
  // partner template access loaded after first render).
  useEffect(() => {
    if (visibleTemplates.length !== 1) return
    const only = visibleTemplates[0]
    const patch: Partial<WizardState> = {}
    if (state.warrantyTemplateUuid !== only.uuid) {
      patch.warrantyTemplateUuid = only.uuid
    }
    if (!state.warrantyTemplateOptionUuid) {
      const optionUuid = getDefaultWarrantyOptionUuid(only, availability)
      if (optionUuid) {
        patch.warrantyTemplateOptionUuid = optionUuid
      }
    }
    if (Object.keys(patch).length > 0) {
      update(patch)
    }
  }, [
    visibleTemplates,
    state.warrantyTemplateUuid,
    state.warrantyTemplateOptionUuid,
    availability,
    update,
  ])

  const resolved = isWarrantySelectionResolved({
    warrantyTemplates: visibleTemplates,
    selectedClassUuid: state.warrantyTemplateUuid,
    selectedOptionUuid: state.warrantyTemplateOptionUuid,
  })

  const noWarranties = visibleTemplates.length === 0

  // Distinct ``unavailable_reason`` messages from per-option /
  // per-template availability decisions. These come from
  // ``AvailabilityRule(scope='warranty_*')`` hits and are why a template
  // can survive server-side filtering only to be hidden from
  // ``visibleTemplates``. Surfaced in the empty state alongside the
  // server-side filter rule names so the user sees the full picture.
  const blockedOptionReasons = useMemo(() => {
    const reasons = new Set<string>()
    for (const wt of permittedTemplates) {
      for (const opt of wt.options) {
        const decision: AvailabilityResult | undefined = getOptionAvailability(
          availability,
          wt.uuid,
          opt.uuid,
        )
        if (decision && !decision.available && decision.unavailable_reason) {
          reasons.add(decision.unavailable_reason)
        }
      }
      const tmplDecision = availability.byTemplate.get(wt.uuid)
      if (tmplDecision?.unavailable_reason) {
        reasons.add(tmplDecision.unavailable_reason)
      }
    }
    return Array.from(reasons)
  }, [permittedTemplates, availability])

  // Did the permission carve-out drop any templates? Only worth
  // mentioning when the user is missing the permission *and* the
  // resolver actually returned at least one EXTENDED_WARRANTY entry.
  const extendedWarrantiesHidden =
    !canRegisterExtended &&
    warrantyTemplates.some((wt) => wt.warranty_type === 'EXTENDED_WARRANTY')

  // Availability rules are evaluated server-side and arrive after the
  // step mounts. Until they resolve we don't yet know which templates
  // are truly selectable, so showing either branch optimistically would
  // flash the full list before collapsing to the empty state when every
  // option is blocked. Render a skeleton while the first evaluation is
  // in flight to avoid that flicker.
  const isResolvingAvailability =
    availability.isLoading && permittedTemplates.length > 0

  // The Continue button is always enabled. The wizard validation
  // context intercepts the click and surfaces an inline hint above
  // the warranty selector when no warranty is selected. The empty
  // state (no available warranties) is treated as a "no required
  // input" branch so the user can still navigate forward.
  const warrantyValid =
    isResolvingAvailability || noWarranties || resolved
  useRegisterValidator('warranty:selected', warrantyValid)
  const { attempted } = useWizardValidation()
  const warrantyError =
    attempted && !warrantyValid && !noWarranties && !isResolvingAvailability
      ? 'Select a warranty to continue'
      : null

  return (
    <div>
      <RegistrationStepGrid>
        <RegistrationPanel
          title="Warranty"
          icon={<ShieldCheck size={14} className="text-[var(--ink-3)]" />}
        >
          {isResolvingAvailability ? (
            <div className="flex flex-col gap-1.5" aria-hidden="true">
              {Array.from({ length: Math.min(permittedTemplates.length, 3) }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2"
                >
                  <div className="h-[13px] w-[40%] rounded-[2px] bg-[var(--skeleton-base)]" />
                  <div className="h-[14px] w-[64px] rounded-full bg-[var(--skeleton-base)]" />
                </div>
              ))}
            </div>
          ) : noWarranties ? (
            !state.selectedProduct ? (
              <p className="text-[13px] text-[var(--ink-3)]">
                Select a product first to see available warranties.
              </p>
            ) : (
              <NoWarrantiesHint
                filterSummary={filterSummary ?? null}
                blockedOptionReasons={blockedOptionReasons}
                extendedWarrantiesHidden={extendedWarrantiesHidden}
              />
            )
          ) : (
            <>
              {warrantyError ? (
                <p className="mb-2 text-xs text-[var(--status-error)]">{warrantyError}</p>
              ) : null}
              <WarrantySelector
                warrantyTemplates={visibleTemplates}
                selectedClassUuid={state.warrantyTemplateUuid}
                selectedOptionUuid={state.warrantyTemplateOptionUuid}
                standalone
                availability={availability}
                onSelectClass={(uuid) => {
                  const nextWarranty = visibleTemplates.find((t) => t.uuid === uuid)
                  update({
                    warrantyTemplateUuid: uuid,
                    warrantyTemplateOptionUuid: getDefaultWarrantyOptionUuid(
                      nextWarranty,
                      availability,
                    ),
                  })
                }}
                onSelectOption={(uuid) => update({ warrantyTemplateOptionUuid: uuid })}
              />
            </>
          )}
        </RegistrationPanel>

        {extraPanels}
      </RegistrationStepGrid>
      <RegistrationStepActions
        onBack={back}
        onContinue={next}
        continueHint={isResolvingAvailability ? null : continueHint}
        withDivider
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty-state hint
// ---------------------------------------------------------------------------

interface NoWarrantiesHintProps {
  filterSummary: WarrantyFilterSummary | null
  blockedOptionReasons: string[]
  extendedWarrantiesHidden: boolean
}

/**
 * Replaces the bare "No warranty templates are available" message with
 * a structured breakdown of *why*: which server-side filter rules
 * matched, which host inputs drove strict-mode resolution, which
 * AvailabilityRule decisions blocked individual options, and whether
 * the current user's permissions hid the rest. Each section is
 * conditional - when none of the diagnostics fire we fall back to the
 * original copy so the panel doesn't grow for the genuinely-empty case.
 */
function NoWarrantiesHint({
  filterSummary,
  blockedOptionReasons,
  extendedWarrantiesHidden,
}: NoWarrantiesHintProps) {
  const baseCount = filterSummary?.base_template_count ?? 0
  const filterMatches = filterSummary?.filter_matches ?? []
  const installationDate = filterSummary?.installation_date ?? null
  const hostAttrs = filterSummary?.host_attrs ?? {}
  const hostAttrEntries = Object.entries(hostAttrs).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  )

  const hasAnyHint =
    filterMatches.length > 0 ||
    blockedOptionReasons.length > 0 ||
    extendedWarrantiesHidden ||
    installationDate !== null ||
    hostAttrEntries.length > 0 ||
    baseCount > 0

  if (!hasAnyHint) {
    return (
      <p className="text-[13px] text-[var(--ink-3)]">
        No warranty templates are available for this product.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-[var(--ink-2)]">
        No warranty templates are available for this product
        {baseCount > 0
          ? ` - ${baseCount} ${baseCount === 1 ? 'template was' : 'templates were'} narrowed to none by the filters below.`
          : '.'}
      </p>

      {filterMatches.length > 0 && (
        <HintSection
          icon={<Filter size={12} className="text-[var(--ink-3)]" />}
          title="Filter rules applied"
        >
          <ul className="flex flex-col gap-1.5">
            {filterMatches.map((match) => (
              <li key={match.rule_uuid} className="flex flex-col gap-0.5">
                <span className="text-[12px] text-[var(--ink-2)]">
                  {match.name}
                </span>
                {describeQueryFilters(match.query_filters).map((line, idx) => (
                  <span
                    key={idx}
                    className="text-[11px] text-[var(--ink-3)] pl-3"
                  >
                    · {line}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </HintSection>
      )}

      {blockedOptionReasons.length > 0 && (
        <HintSection title="Options blocked by availability rules">
          <ul className="flex flex-col gap-1">
            {blockedOptionReasons.map((reason, idx) => (
              <li key={idx} className="text-[11px] text-[var(--ink-3)]">
                · {reason}
              </li>
            ))}
          </ul>
        </HintSection>
      )}

      {extendedWarrantiesHidden && (
        <HintSection title="Permission">
          <p className="text-[11px] text-[var(--ink-3)]">
            Extended warranties are hidden because your account is missing
            the <code>warranties.register_extended</code> permission.
          </p>
        </HintSection>
      )}

      {(installationDate || hostAttrEntries.length > 0) && (
        <HintSection title="Inputs used for filtering">
          <ul className="flex flex-col gap-1 text-[11px] text-[var(--ink-3)]">
            {installationDate && (
              <li>
                Installation date:{' '}
                <span className="text-[var(--ink-2)]">{installationDate}</span>
              </li>
            )}
            {hostAttrEntries.map(([key, value]) => (
              <li key={key}>
                {key}:{' '}
                <span className="text-[var(--ink-2)]">
                  {formatHostAttrValue(value)}
                </span>
              </li>
            ))}
          </ul>
        </HintSection>
      )}
    </div>
  )
}

function HintSection({
  icon,
  title,
  children,
}: {
  icon?: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {icon}
        <span>{title}</span>
      </div>
      {children}
    </div>
  )
}

/**
 * Render a `WarrantyAttributeFilter`-shaped value as a short readable
 * string. Strings/numbers/booleans pass through; arrays fan out as a
 * comma-joined list; objects fall back to JSON so we don't silently
 * drop diagnostic detail.
 */
function formatHostAttrValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (Array.isArray(value)) return value.map(formatHostAttrValue).join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * Translate a rule's ``query_filters`` payload into bullet-point lines
 * the user can read. Empty buckets are skipped so a rule that only
 * filters by category doesn't emit blank "warranty types: none" rows.
 */
function describeQueryFilters(
  filters: Record<string, unknown> | undefined,
): string[] {
  if (!filters) return []
  const lines: string[] = []

  const warrantyTypes = filters.warranty_types
  if (Array.isArray(warrantyTypes) && warrantyTypes.length > 0) {
    lines.push(
      `warranty type ${warrantyTypes.length === 1 ? 'is' : 'in'} ${warrantyTypes
        .map((t) => formatWarrantyTypeLabel(String(t)))
        .join(', ')}`,
    )
  }

  const categoryIds = filters.category_ids
  if (Array.isArray(categoryIds) && categoryIds.length > 0) {
    lines.push(
      `category ${categoryIds.length === 1 ? 'is' : 'in'} ${categoryIds.length} selected`,
    )
  }

  const templateIds = filters.template_ids
  if (Array.isArray(templateIds) && templateIds.length > 0) {
    lines.push(
      `template ${templateIds.length === 1 ? 'is' : 'in'} ${templateIds.length} selected`,
    )
  }

  const attributeFilters = filters.attribute_filters
  if (Array.isArray(attributeFilters)) {
    for (const af of attributeFilters) {
      if (af && typeof af === 'object') {
        const codeName = (af as { code_name?: unknown }).code_name
        const value = (af as { value?: unknown }).value
        if (codeName && value !== undefined && value !== null && value !== '') {
          lines.push(
            `${String(codeName)} = ${formatHostAttrValue(value)}`,
          )
        }
      }
    }
  }

  const geoZoneIds = filters.geo_zone_ids
  if (Array.isArray(geoZoneIds) && geoZoneIds.length > 0) {
    lines.push(
      `geographic zone ${geoZoneIds.length === 1 ? 'is' : 'in'} ${geoZoneIds.length} selected`,
    )
  }

  return lines
}
